import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Loader2, Eye, ScanLine, MousePointerClick, UserPlus, TrendingUp, Heart, MessageCircle, Megaphone, ArrowLeft, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const RANGES = [{ d: 7, label: "7 days" }, { d: 30, label: "30 days" }, { d: 90, label: "90 days" }];
const fmtDay = (iso) => { const [, m, d] = iso.split("-"); return `${m}/${d}`; };

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-saddle/20 bg-white rounded-xl p-4" data-testid={testid}>
      <Icon size={16} className="text-saddle" />
      <p className="font-serif text-3xl text-forest mt-2 tabular-nums">{(value ?? 0).toLocaleString()}</p>
      <p className="text-xs text-charcoal/60 mt-0.5">{label}</p>
      {sub != null && <p className="text-[11px] text-charcoal/45 mt-0.5">{sub.toLocaleString()} all-time</p>}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-forest text-bone text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => <p key={p.dataKey} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>)}
    </div>
  );
}

export default function PartnerAnalytics() {
  const { isAuthed, ready } = useAuth();
  const { setAuthOpen } = useUI();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ready || !isAuthed) return;
    setLoading(true);
    api.partnerAnalyticsTimeseries(days).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [ready, isAuthed, days]);

  if (!ready) return <Shell><Center><Loader2 className="animate-spin text-forest" /></Center></Shell>;
  if (!isAuthed) return (
    <Shell><Center><div className="text-center" data-testid="analytics-signin-gate">
      <h1 className="font-serif text-3xl text-forest">Partner Analytics</h1>
      <button onClick={() => setAuthOpen(true)} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full font-semibold" data-testid="analytics-signin-btn">Sign in</button>
    </div></Center></Shell>
  );
  if (data === undefined) return <Shell><Center><Loader2 className="animate-spin text-forest" /></Center></Shell>;
  if (!data || !data.series) return (
    <Shell><Center><div className="text-center" data-testid="analytics-no-partner">
      <h1 className="font-serif text-3xl text-forest">No partner profile yet</h1>
      <button onClick={() => navigate("/partner/onboarding")} className="mt-5 bg-forest text-bone px-6 py-3 rounded-full font-semibold" data-testid="analytics-onboarding">Start onboarding</button>
    </div></Center></Shell>
  );

  const t = data.totals || {};
  const at = data.all_time || {};
  const eng = data.engagement || {};
  const chartData = (data.series || []).map((s) => ({ ...s, day: fmtDay(s.date) }));
  const funnel = [
    { stage: "Referral clicks", value: t.referral_clicks || 0 },
    { stage: "Signups", value: t.signups || 0 },
    { stage: "Conversions", value: t.conversions || 0 },
  ];
  const maxSrc = Math.max(1, ...(data.sources || []).map((s) => s.count));

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[1100px] mx-auto">
        <button onClick={() => navigate("/partner/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-charcoal/50 hover:text-forest mb-3" data-testid="analytics-back"><ArrowLeft size={14} /> Dashboard</button>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Analytics</span>
            <h1 className="font-serif text-3xl text-forest mt-1">Performance</h1>
            <p className="text-sm text-charcoal/60 mt-1">Views, QR scans, referral conversions and update engagement.</p>
          </div>
          <div className="flex items-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin text-saddle" data-testid="analytics-loading" />}
            <div className="flex gap-1 bg-white border border-saddle/20 rounded-full p-1" data-testid="range-selector">
            {RANGES.map((r) => (
              <button key={r.d} onClick={() => setDays(r.d)} className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${days === r.d ? "bg-forest text-bone" : "text-charcoal/60 hover:text-forest"}`} data-testid={`range-${r.d}`}>{r.label}</button>
            ))}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Kpi icon={Eye} label="Profile views" value={t.views} sub={at.views} testid="kpi-views" />
          <Kpi icon={ScanLine} label="QR scans" value={t.qr_scans} sub={at.qr_scans} testid="kpi-qr-scans" />
          <Kpi icon={MousePointerClick} label="Referral clicks" value={t.referral_clicks} sub={at.referral_clicks} testid="kpi-referral-clicks" />
          <Kpi icon={UserPlus} label="Referral signups" value={t.signups} sub={at.signups} testid="kpi-signups" />
        </div>

        {/* Traffic over time */}
        <div className="mt-6 border border-saddle/20 bg-white rounded-xl p-5" data-testid="traffic-chart">
          <h2 className="font-serif text-lg text-forest flex items-center gap-2"><TrendingUp size={17} className="text-blaze" /> Traffic over time</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1F2A22" stopOpacity={0.35} /><stop offset="100%" stopColor="#1F2A22" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gScans" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF6A00" stopOpacity={0.35} /><stop offset="100%" stopColor="#FF6A00" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8A857C" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8A857C" }} allowDecimals={false} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="views" name="Views" stroke="#1F2A22" strokeWidth={2} fill="url(#gViews)" isAnimationActive={false} />
                <Area type="monotone" dataKey="qr_scans" name="QR scans" stroke="#FF6A00" strokeWidth={2} fill="url(#gScans)" isAnimationActive={false} />
                <Area type="monotone" dataKey="referral_clicks" name="Referral clicks" stroke="#3A7D44" strokeWidth={2} fillOpacity={0} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-3 text-xs text-charcoal/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-forest" /> Views</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blaze" /> QR scans</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#3A7D44]" /> Referral clicks</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Referral funnel */}
          <div className="border border-saddle/20 bg-white rounded-xl p-5" data-testid="funnel-chart">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-lg text-forest">Referral funnel</h2>
              {(t.conversion_value || 0) > 0 && <span className="inline-flex items-center gap-1 text-sm font-semibold text-forest" data-testid="conversion-value"><DollarSign size={14} />{(t.conversion_value / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })} attributed value</span>}
            </div>
            <div className="h-52 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#8A857C" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8A857C" }} allowDecimals={false} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "#F3EFE6" }} />
                  <Bar dataKey="value" name="Count" fill="#FF6A00" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top sources */}
          <div className="border border-saddle/20 bg-white rounded-xl p-5" data-testid="sources-chart">
            <h2 className="font-serif text-lg text-forest">Top traffic sources</h2>
            <div className="mt-4 space-y-3">
              {(data.sources || []).length === 0 ? <p className="text-sm text-charcoal/50">No traffic yet in this period.</p> :
                data.sources.map((s) => (
                  <div key={s.src} data-testid={`source-${s.src}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="capitalize text-charcoal/75">{s.src}</span><span className="font-semibold text-forest tabular-nums">{s.count}</span></div>
                    <div className="h-2 bg-paper rounded-full overflow-hidden"><div className="h-full bg-forest rounded-full" style={{ width: `${(s.count / maxSrc) * 100}%` }} /></div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Update engagement */}
        <div className="mt-6 border border-saddle/20 bg-white rounded-xl p-5" data-testid="engagement-section">
          <h2 className="font-serif text-lg text-forest flex items-center gap-2"><Megaphone size={17} className="text-blaze" /> Update engagement</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Kpi icon={Megaphone} label="Updates posted" value={eng.updates_total} testid="eng-updates" />
            <Kpi icon={TrendingUp} label={`Posted (${days}d)`} value={eng.updates_in_period} testid="eng-updates-period" />
            <Kpi icon={Heart} label="Total likes" value={eng.likes} testid="eng-likes" />
            <Kpi icon={MessageCircle} label="Total comments" value={eng.comments} testid="eng-comments" />
          </div>
        </div>
      </motion.div>
    </Shell>
  );
}

function Center({ children }) { return <div className="py-32 grid place-items-center">{children}</div>; }
function Shell({ children }) {
  return (
    <div className="bg-paper min-h-screen" data-testid="partner-analytics">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">{children}</main>
      <Footer />
    </div>
  );
}
